import { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/system';
import orderBy from 'lodash.orderby';
import AddIcon from '@mui/icons-material/AddRounded';
import EditIcon from '@mui/icons-material/EditRounded';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import FieldWithActions from '@components/FieldWithActions';
import { Typography } from '@components/Typography';
import { ModalConfirm } from '@components/Modal';
import Button from '@components/Button';
import { Input } from '@components/Input';
import { useTranslation } from '@hooks';
import { ProfileType, ProfileName, isListingCoveredByOfficial, SocialDisplayProfiles } from '@constants/integrations';
import { alertActions, accountActions, locationsProfilesActions } from '@actions';
import { companyService } from '@services';
import { handleApiError } from '@helpers/api';
import GeneralConnect, { PROFILE_MODE } from './GeneralConnect';

const Container = styled('div')({
    maxWidth: 696,
    '@media (max-width: 1024px)': {
        maxWidth: 'none',
    },
});

const WildCardProfiles = [
    ProfileName.GoogleOfficial,
    ProfileName.FacebookOfficial,
    ProfileName.InstagramOfficial,
    ProfileName.CoverManager,
    ProfileName.iFood,
    ProfileName.VTEX,
];

const Profiles = ({ selectedCompany, setSelectedCompany }) => {
    const [profile, setProfile] = useState({ open: false, data: null, mode: PROFILE_MODE.ADD });
    const [deleteModal, setDeleteModal] = useState({ open: false, id: null });

    const profilesSlice = useSelector(state => state.locationsProfiles);
    const locationsProfiles = profilesSlice?.ownerId === selectedCompany.companyId ? profilesSlice?.data || [] : [];
    const { t } = useTranslation();
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(locationsProfilesActions.get(selectedCompany.companyId));
    }, [selectedCompany.companyId]);

    const PROFILES = useMemo(() => {
        return orderBy(
            locationsProfiles
                .map(p => {
                    const isShared = p.shareSource === 'location';
                    return {
                        ...p,
                        isShared,
                        disabled:
                            isListingCoveredByOfficial(selectedCompany.profiles, p.type) ||
                            // X/TikTok reuse their listing type, so the integration link marks official.
                            ((p.type === ProfileName.X || p.type === ProfileName.Tiktok) &&
                                !!p.locationIntegrationId) ||
                            isShared,
                    };
                })
                .filter(
                    p =>
                        !WildCardProfiles.includes(p.type) &&
                        !SocialDisplayProfiles.includes(p.type) &&
                        p.type !== ProfileName.Yext,
                ),
            ['isShared', 'type'],
            ['desc', 'asc'],
        );
    }, [locationsProfiles, selectedCompany]);

    const onAddProfileClick = () => setProfile(prev => ({ ...prev, open: true, mode: PROFILE_MODE.ADD }));

    const onDeleteProfileClick = async () => {
        try {
            await companyService.deleteProfile(selectedCompany.companyId, deleteModal.id);
            setSelectedCompany(prev => ({
                ...prev,
                profiles: prev?.profiles?.filter(p => p.profileId !== deleteModal.id),
            }));
            await dispatch(locationsProfilesActions.get(selectedCompany.companyId));
            await dispatch(accountActions.get());
            setDeleteModal(prev => ({ ...prev, open: false, id: null }));
            dispatch(alertActions.success(t('alertMessages.deleteSuccess')));
        } catch (error) {
            handleApiError(error, 'alertMessages.deleteFail');
        }
    };

    const onEditProfileClick = data => setProfile(prev => ({ ...prev, open: true, data, mode: PROFILE_MODE.EDIT }));

    return (
        <Container>
            <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}
            >
                <Typography
                    variant="subtitle1"
                    sx={{
                        color: 'var(--text-primary)',
                        '@media (max-width: 500px)': {
                            maxWidth: '180px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        },
                    }}
                >
                    {t('SettingsCompanies.reviewPlatforms')}
                </Typography>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={onAddProfileClick}>
                    {t('SettingsCompanies.addReviewPlatform')}
                </Button>
            </div>
            <div style={{ display: 'grid', width: '100%', gridTemplateColumns: '100%', gridGap: '24px' }}>
                {PROFILES.map((p, index) => (
                    <FieldWithActions
                        key={`${p.type}-${index}`}
                        actions={
                            !p.disabled
                                ? [
                                      {
                                          icon: <EditIcon />,
                                          label: t('buttons.edit'),
                                          onClick: () => onEditProfileClick(p),
                                      },
                                      {
                                          icon: <DeleteIcon />,
                                          label: t('buttons.delete'),
                                          onClick: () =>
                                              setDeleteModal(prev => ({ ...prev, open: true, id: p.profileId })),
                                      },
                                  ]
                                : // A profile shared from another location can be removed here but not edited.
                                  p.shareSource === 'location'
                                  ? [
                                        {
                                            icon: <DeleteIcon />,
                                            label: t('buttons.delete'),
                                            onClick: () =>
                                                setDeleteModal(prev => ({ ...prev, open: true, id: p.profileId })),
                                        },
                                    ]
                                  : []
                        }
                    >
                        <Input
                            inputProps={{ value: p.businessUrl }}
                            label={`${ProfileType[p.type]}${p.isShared ? ` (${t('SettingsBrands.shared')})` : ''}`}
                            disabled
                        />
                    </FieldWithActions>
                ))}
            </div>
            {profile.open ? (
                <GeneralConnect
                    locationsProfiles={locationsProfiles}
                    profile={profile}
                    setProfile={setProfile}
                    selectedCompany={selectedCompany}
                    setSelectedCompany={setSelectedCompany}
                />
            ) : null}
            {deleteModal.open && (
                <ModalConfirm
                    isOpen={deleteModal.open}
                    handleClose={() => setDeleteModal(prev => ({ ...prev, open: false, id: null }))}
                    title={t('common.deleteTitle')}
                    description={t('common.deleteDescription')}
                    onPrimaryAction={onDeleteProfileClick}
                    primaryActionText={t('buttons.delete')}
                    primaryActionType="danger"
                />
            )}
        </Container>
    );
};

export default Profiles;
